import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../api/SheetsApi";
import type {
  Sheet,
  SheetCategory,
  SheetColumn,
  SheetRow,
  SheetShare,
  SheetCheckpoint,
  AssignableEmployee,
  SimilarMatch,
  SheetColumnType,
  SharePermission,
  CsvImportPreview,
} from "../api/SheetsApi";
import { RowConflictError } from "../api/SheetsApi";
import { VerifyContext } from "../../../app/VerifyContext";

const PAGE_SIZE = 20;
const ROWS_PAGE_SIZE = 100;

interface SheetsContextValue {
  isAdmin: boolean;
  // Whether the current user can edit currentSheet's content — admin, or an
  // employee shared with "edit" permission. Computed once here rather than
  // recomputed per-component so the rule can't silently diverge between them.
  canEdit: boolean;
  // Column structure (rename/type/options/delete/drag-reorder/resize) is
  // admin-only on top of canEdit — employees never manage columns even with
  // edit share. Centralized for the same reason as canEdit above.
  canManageColumns: boolean;

  // list
  sheets: Sheet[];
  totalCount: number;
  totalPages: number;
  page: number;
  loading: boolean;
  error: string;
  search: string;
  categoryFilter: number | null;
  tagFilter: string | null;
  setPage: (p: number) => void;
  setSearch: (s: string) => void;
  setCategoryFilter: (id: number | null) => void;
  setTagFilter: (tag: string | null) => void;
  refresh: () => Promise<void>;

  // categories
  categories: SheetCategory[];
  refreshCategories: () => Promise<void>;
  createCategory: (name: string, description?: string) => Promise<SheetCategory>;
  renameCategory: (id: number, name: string) => Promise<void>;
  removeCategory: (id: number) => Promise<void>;
  checkSimilarSheets: (name: string) => Promise<SimilarMatch[]>;
  checkSimilarCategories: (name: string) => Promise<SimilarMatch[]>;

  // create / delete sheet
  createSheet: (data: { name: string; description?: string; category_id?: number | null; tags?: string[] }) => Promise<Sheet>;
  deleteSheet: (id: number) => Promise<void>;

  // detail
  currentSheet: Sheet | null;
  columns: SheetColumn[];
  shares: SheetShare[];
  assignableEmployees: AssignableEmployee[];
  rows: SheetRow[];
  rowsPage: number;
  rowsTotalPages: number;
  rowConflict: SheetRow | null;
  clearRowConflict: () => void;
  acceptRowConflict: () => void;
  detailLoading: boolean;
  detailError: string;
  saveStatus: "idle" | "saving" | "saved" | "error";
  // Detail behind a saveStatus of "error" — same reasoning as
  // manualSaveError below.
  saveError: string | null;
  lastSavedAt: Date | null;
  hasUnsavedChanges: boolean;
  manualSaveStatus: "idle" | "saving" | "saved" | "error";
  // Detail behind a manualSaveStatus of "error" — set only for a
  // non-conflict save failure (RowConflictError has its own banner UI).
  manualSaveError: string | null;
  saveNow: () => Promise<boolean>;
  toggleAutoSave: (enabled: boolean) => Promise<void>;
  toggleReadonly: (enabled: boolean) => Promise<void>;

  loadSheetDetail: (id: number) => Promise<void>;
  updateSheetMeta: (data: { name?: string; description?: string; category_id?: number | null; tags?: string[] }) => Promise<void>;
  setRowsPage: (p: number) => void;

  addColumn: (data: { key: string; label: string; type: SheetColumnType; select_options?: string[]; width?: number }) => Promise<SheetColumn | null>;
  editColumn: (columnId: number, data: { label?: string; type?: SheetColumnType; select_options?: string[]; width?: number }) => Promise<void>;
  removeColumn: (columnId: number) => Promise<void>;
  reorderColumns: (columnIds: number[]) => Promise<void>;

  addRow: () => Promise<SheetRow | null>;
  pasteNewRows: (rowsData: Record<string, unknown>[]) => Promise<void>;
  editRow: (rowId: number, data: Record<string, unknown>, version: number) => Promise<void>;
  removeRow: (rowId: number) => Promise<void>;
  restoreRow: (rowId: number) => Promise<void>;
  reorderRows: (rowIds: number[]) => Promise<void>;
  editRowHeight: (rowId: number, height: number | null) => Promise<void>;

  importCsvPreview: (file: File) => Promise<CsvImportPreview>;
  importCsvCommit: (file: File) => Promise<CsvImportPreview>;
  triggerExportXlsx: () => Promise<void>;

  loadShares: () => Promise<void>;
  addShares: (employeeIds: number[], permission: SharePermission) => Promise<void>;
  editSharePermission: (shareId: number, permission: SharePermission) => Promise<void>;
  removeShare: (shareId: number) => Promise<void>;
  loadAssignableEmployees: () => Promise<void>;

  checkpoints: SheetCheckpoint[];
  loadCheckpoints: () => Promise<void>;
  saveCheckpoint: (label: string | null) => Promise<void>;
  removeCheckpoint: (checkpointId: number) => Promise<void>;
  purgeCheckpoints: (olderThanDays: number) => Promise<number>;
  restoreCheckpoint: (checkpointId: number) => Promise<void>;
}

const SheetsContext = createContext<SheetsContextValue | null>(null);

export function SheetsProvider({ children }: { children: React.ReactNode }) {
  const { superAdmin } = useContext(VerifyContext);
  const isAdmin = superAdmin;

  // ── list state ──────────────────────────────────────────────────────
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearchState] = useState("");
  const [categoryFilter, setCategoryFilterState] = useState<number | null>(null);
  const [tagFilter, setTagFilterState] = useState<string | null>(null);
  const [categories, setCategories] = useState<SheetCategory[]>([]);

  const setSearch = useCallback((s: string) => { setSearchState(s); setPage(1); }, []);
  const setCategoryFilter = useCallback((id: number | null) => { setCategoryFilterState(id); setPage(1); }, []);
  const setTagFilter = useCallback((tag: string | null) => { setTagFilterState(tag); setPage(1); }, []);

  const reqSeq = useRef(0);

  const refresh = useCallback(async () => {
    const reqId = ++reqSeq.current;
    setLoading(true);
    setError("");
    try {
      const res = await api.listSheets({
        search: search.trim() || undefined,
        category_id: categoryFilter ?? undefined,
        tag: tagFilter ?? undefined,
        page,
        page_size: PAGE_SIZE,
      });
      if (reqId !== reqSeq.current) return;
      setSheets(res.items);
      setTotalCount(res.total_count);
      setTotalPages(res.total_pages);
      if (page > res.total_pages && res.total_pages > 0) setPage(res.total_pages);
    } catch {
      if (reqId === reqSeq.current) setError("Failed to load sheets");
    } finally {
      if (reqId === reqSeq.current) setLoading(false);
    }
  }, [search, categoryFilter, tagFilter, page]);

  const refreshCategories = useCallback(async () => {
    try {
      setCategories(await api.listCategories());
    } catch {
      // non-fatal — filters/labels just show fewer options
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { refreshCategories(); }, [refreshCategories]);

  const createCategory = useCallback(async (name: string, description?: string) => {
    const cat = await api.createCategory({ name, description });
    await refreshCategories();
    return cat;
  }, [refreshCategories]);

  const renameCategory = useCallback(async (id: number, name: string) => {
    await api.updateCategory(id, { name });
    await refreshCategories();
  }, [refreshCategories]);

  const removeCategory = useCallback(async (id: number) => {
    await api.deleteCategory(id);
    await Promise.all([refreshCategories(), refresh()]);
  }, [refreshCategories, refresh]);

  const checkSimilarSheets = useCallback((name: string) => api.checkSimilarSheets(name), []);
  const checkSimilarCategories = useCallback((name: string) => api.checkSimilarCategories(name), []);

  const createSheet = useCallback(async (data: { name: string; description?: string; category_id?: number | null; tags?: string[] }) => {
    const sheet = await api.createSheet(data);
    await refresh();
    return sheet;
  }, [refresh]);

  const deleteSheet = useCallback(async (id: number) => {
    await api.deleteSheet(id);
    await refresh();
  }, [refresh]);

  // ── detail state ────────────────────────────────────────────────────
  const [currentSheet, setCurrentSheet] = useState<Sheet | null>(null);
  // Read-only mode (admin toggle) locks the sheet for everyone, admin
  // included — that's the whole point (protects against the admin's own
  // accidental edits), so it's folded into this single gate rather than
  // requiring every call site to separately check both.
  const canEdit = (isAdmin || currentSheet?.my_permission === "edit") && !currentSheet?.is_readonly;
  // Written directly in terms of the real invariant (columns are admin-only,
  // full stop) rather than as `isAdmin && canEdit` — that form reads as if
  // canEdit's employee-edit-share branch might matter here, which it never
  // does, since an employee can never manage columns even with edit access.
  const canManageColumns = isAdmin && !currentSheet?.is_readonly;
  const [columns, setColumns] = useState<SheetColumn[]>([]);
  const [shares, setShares] = useState<SheetShare[]>([]);
  const [assignableEmployees, setAssignableEmployees] = useState<AssignableEmployee[]>([]);
  const [checkpoints, setCheckpoints] = useState<SheetCheckpoint[]>([]);
  const [rows, setRows] = useState<SheetRow[]>([]);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsTotalPages, setRowsTotalPages] = useState(1);
  const [rowConflict, setRowConflict] = useState<SheetRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  // Rows with local-only edits not yet pushed to the server — only ever
  // populated while currentSheet.auto_save_enabled is false.
  // NOT multi-editor safe: these edits exist only in this tab's memory, so
  // another editor re-enabling auto-save server-side (toggleAutoSave) has no
  // way to know about them — accepted tradeoff, assumes one editor at a time.
  const [dirtyRowIds, setDirtyRowIds] = useState<Set<number>>(new Set());
  const hasUnsavedChanges = dirtyRowIds.size > 0;
  // dirtyRowIdsRef mirrors dirtyRowIds synchronously (updated in the same
  // tick as setDirtyRowIds below, never via a useEffect) so callbacks that
  // must stay stable identities (loadRowsPage, saveNow, toggleAutoSave) can
  // read the true current dirty set without depending on React having
  // already re-rendered — a useEffect-based mirror can lag behind a
  // same-gesture event pair (e.g. a cell's blur commit immediately followed
  // by a click on another control) by a full render.
  //
  // dirtyEditsRef holds the actual pending {data, version} per dirty row,
  // decoupled from the paginated `rows` array — `rows` only ever holds the
  // currently-displayed ~100-row page, so without this a dirty row's edit
  // would vanish outright the moment the user pages away and back (the
  // fresh page fetch has no record of it at all, unlike a same-page reload
  // where the row is at least still present in state to preserve).
  const dirtyRowIdsRef = useRef<Set<number>>(new Set());
  const dirtyEditsRef = useRef<Map<number, { data: Record<string, unknown>; version: number }>>(new Map());

  const markRowDirty = useCallback((rowId: number, data: Record<string, unknown>, version: number) => {
    dirtyEditsRef.current.set(rowId, { data, version });
    if (!dirtyRowIdsRef.current.has(rowId)) {
      dirtyRowIdsRef.current = new Set(dirtyRowIdsRef.current).add(rowId);
      setDirtyRowIds(dirtyRowIdsRef.current);
    }
  }, []);

  const clearRowDirty = useCallback((rowId: number) => {
    dirtyEditsRef.current.delete(rowId);
    if (dirtyRowIdsRef.current.has(rowId)) {
      const next = new Set(dirtyRowIdsRef.current);
      next.delete(rowId);
      dirtyRowIdsRef.current = next;
      setDirtyRowIds(next);
    }
  }, []);

  const clearAllDirty = useCallback(() => {
    dirtyEditsRef.current = new Map();
    dirtyRowIdsRef.current = new Set();
    setDirtyRowIds(new Set());
  }, []);

  // Shared by every call site that receives a fresh row from the server
  // (a page reload, a height update) and must not clobber a still-pending
  // local edit on that same row. `data` AND `version` both come from the
  // pending edit, never the server — adopting a fresher server version
  // here would decouple the optimistic-concurrency token from the (stale,
  // unsaved) data it's supposed to protect, letting a later save pass its
  // version check while silently overwriting a concurrent editor's real
  // change.
  function withDirtyOverlay(serverRow: SheetRow): SheetRow {
    const dirty = dirtyEditsRef.current.get(serverRow.id);
    return dirty ? { ...serverRow, data: dirty.data, version: dirty.version } : serverRow;
  }

  // Sheets-style "Saving… / Last saved at HH:MM:SS" indicator — tracks
  // every grid mutation (cell edits, row/column add/remove, paste), not
  // just plain cell commits, since all of those hit the same save API.
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  // Detail behind a saveStatus of "error" — same reasoning as
  // manualSaveError below, but for trackSave's immediate-write path (auto-
  // save on, or any structural mutation) rather than the manual Save button.
  const [saveError, setSaveError] = useState<string | null>(null);

  // Separate from saveStatus above: row/column structural mutations
  // (addRow, editColumn, ...) always save immediately via trackSave
  // regardless of auto-save, so if the manual Save button read saveStatus
  // too, an unrelated resize/add mid-flush could flip its spinner/disabled
  // state or overwrite a genuine saveNow() failure with a spurious "saved".
  const [manualSaveStatus, setManualSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  // Populated only on a non-conflict save failure (e.g. the sheet went
  // read-only mid-session) — RowConflictError already has its own dedicated
  // UI (the conflict banner), so this is specifically for the "why did my
  // save just fail with no explanation" gap a bare console.error left.
  const [manualSaveError, setManualSaveError] = useState<string | null>(null);

  const trackSave = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setSaveStatus("saving");
    setSaveError(null);
    try {
      const result = await fn();
      setSaveStatus("saved");
      setLastSavedAt(new Date());
      return result;
    } catch (err: any) {
      setSaveStatus("error");
      // editRow's own catch below overrides both back to "idle"/null for a
      // RowConflictError, which already has its own dedicated banner UI.
      setSaveError(err?.response?.data?.detail || err?.message || "Failed to save changes");
      throw err;
    }
  }, []);

  const clearRowConflict = useCallback(() => {
    setRowConflict((conflict) => {
      if (conflict) {
        // Dismissing (rather than accepting) still means giving up on this
        // specific pending edit — its cached version is now stale, and
        // loadRowsPage's dirty-overlay would otherwise keep silently
        // re-imposing that stale version forever, so every future save
        // attempt for this row would 409 indefinitely with no way out
        // short of accepting (which discards the edit anyway).
        clearRowDirty(conflict.id);
      }
      return null;
    });
  }, [clearRowDirty]);
  const acceptRowConflict = useCallback(() => {
    setRowConflict((conflict) => {
      if (conflict) {
        setRows((prev) => prev.map((r) => (r.id === conflict.id ? conflict : r)));
        // Accepting the conflict means "take the server's version" — the
        // local unsaved edit that caused the conflict is deliberately
        // discarded, so it must stop being reported as still-unsaved.
        clearRowDirty(conflict.id);
      }
      return null;
    });
  }, [clearRowDirty]);

  const currentSheetIdRef = useRef<number | null>(null);

  const loadRowsPage = useCallback(async (sheetId: number, targetPage: number) => {
    const res = await api.listRows(sheetId, targetPage, ROWS_PAGE_SIZE);
    if (currentSheetIdRef.current !== sheetId) return;
    // Structural mutations (add/delete/restore/reorder row, paste, CSV
    // import, and plain re-paging) all reload the current page from the
    // server — without this, that reload would silently overwrite any
    // locally-dirty row's unsaved `data` with the server's pre-edit copy.
    // withDirtyOverlay is sourced from dirtyEditsRef (not the outgoing
    // `rows` array) so a dirty row's edit survives even paging away to a
    // different page and back — `rows` only ever holds the
    // currently-displayed page, so a row that isn't part of `res.items`
    // this time would otherwise be dropped outright rather than merely
    // overwritten.
    setRows(res.items.map(withDirtyOverlay));
    setRowsTotalPages(res.total_pages || 1);
  }, []);

  const loadSheetDetail = useCallback(async (id: number) => {
    currentSheetIdRef.current = id;
    setDetailLoading(true);
    setDetailError("");
    setRowConflict(null);
    clearAllDirty();
    setRowsPage(1);
    try {
      const [sheet, cols] = await Promise.all([api.getSheet(id), api.listColumns(id)]);
      if (currentSheetIdRef.current !== id) return;
      setCurrentSheet(sheet);
      setColumns(cols.slice().sort((a, b) => a.position - b.position));
      await loadRowsPage(id, 1);
    } catch {
      if (currentSheetIdRef.current === id) setDetailError("Failed to load sheet");
    } finally {
      if (currentSheetIdRef.current === id) setDetailLoading(false);
    }
  }, [loadRowsPage, clearAllDirty]);

  useEffect(() => {
    if (currentSheet) loadRowsPage(currentSheet.id, rowsPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPage]);

  const updateSheetMeta = useCallback(async (data: { name?: string; description?: string; category_id?: number | null; tags?: string[] }) => {
    if (!currentSheet) return;
    const updated = await api.updateSheet(currentSheet.id, data);
    setCurrentSheet((prev) => (prev ? { ...prev, ...updated } : updated));
    await refresh();
  }, [currentSheet, refresh]);

  const reloadColumns = useCallback(async () => {
    if (!currentSheet) return;
    setColumns((await api.listColumns(currentSheet.id)).slice().sort((a, b) => a.position - b.position));
  }, [currentSheet]);

  const addColumn = useCallback(async (data: { key: string; label: string; type: SheetColumnType; select_options?: string[]; width?: number }) => {
    if (!currentSheet) return null;
    const created = await trackSave(() => api.createColumn(currentSheet.id, data));
    await reloadColumns();
    return created;
  }, [currentSheet, reloadColumns, trackSave]);

  const editColumn = useCallback(async (columnId: number, data: { label?: string; type?: SheetColumnType; select_options?: string[]; width?: number }) => {
    if (!currentSheet) return;
    await trackSave(() => api.updateColumn(currentSheet.id, columnId, data));
    await reloadColumns();
  }, [currentSheet, reloadColumns, trackSave]);

  const removeColumn = useCallback(async (columnId: number) => {
    if (!currentSheet) return;
    await trackSave(() => api.deleteColumn(currentSheet.id, columnId));
    await reloadColumns();
  }, [currentSheet, reloadColumns, trackSave]);

  // Drag-and-drop column reordering — must be the complete set of active
  // column ids in their new order (see reorder_columns on the backend).
  const reorderColumns = useCallback(async (columnIds: number[]) => {
    if (!currentSheet) return;
    await trackSave(() => api.reorderColumns(currentSheet.id, columnIds));
    await reloadColumns();
  }, [currentSheet, reloadColumns, trackSave]);

  const addRow = useCallback(async () => {
    if (!currentSheet) return null;
    const created = await trackSave(() => api.createRow(currentSheet.id, {}));
    await loadRowsPage(currentSheet.id, rowsPage);
    return created;
  }, [currentSheet, loadRowsPage, rowsPage, trackSave]);

  // Used by a multi-cell paste that overflows past the current row count —
  // creates the extra rows with their pasted data already populated.
  const pasteNewRows = useCallback(async (rowsData: Record<string, unknown>[]) => {
    if (!currentSheet || rowsData.length === 0) return;
    await trackSave(() => api.bulkCreateRows(currentSheet.id, rowsData));
    await loadRowsPage(currentSheet.id, rowsPage);
  }, [currentSheet, loadRowsPage, rowsPage, trackSave]);

  const editRow = useCallback(async (rowId: number, data: Record<string, unknown>, version: number) => {
    if (!currentSheet) return;
    if (!currentSheet.auto_save_enabled) {
      // Held locally until the user clicks Save — no network call, and the
      // row's `version` is left untouched since only the server ever bumps it.
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, data } : r)));
      markRowDirty(rowId, data, version);
      return;
    }
    try {
      const updated = await trackSave(() => api.updateRow(currentSheet.id, rowId, data, version));
      setRows((prev) => prev.map((r) => (r.id === rowId ? updated : r)));
      // Defensive: this row shouldn't normally be dirty while auto-save is
      // on, but if it somehow is (e.g. a toggle-on raced a fresh edit),
      // this immediate save just persisted it — let it self-heal rather
      // than stay stuck reporting unsaved forever with an increasingly
      // stale cached version.
      clearRowDirty(rowId);
    } catch (err) {
      if (err instanceof RowConflictError) {
        setRowConflict(err.currentRow);
        setSaveStatus("idle"); // the conflict banner already communicates this — no need for a second, scarier indicator
        setSaveError(null);
        return;
      }
      throw err;
    }
  }, [currentSheet, trackSave, markRowDirty, clearRowDirty]);

  // Coalesces overlapping saveNow calls — e.g. the manual Save button
  // clicked, then a row edited again or the auto-save toggle flipped on
  // before that pass finishes (toggleAutoSave also calls saveNow). A plain
  // "return the same in-flight promise" guard isn't enough on its own: if a
  // new row goes dirty *after* a pass already snapshotted its id list, that
  // caller would be told "fully flushed" when it wasn't. So a concurrent
  // call instead flags rerunRequested and awaits the same promise; the
  // running loop checks that flag after each pass and does another pass —
  // against the then-current dirty set — before actually resolving.
  const saveNowStateRef = useRef<{ promise: Promise<boolean>; rerunRequested: boolean } | null>(null);

  // Flushes every locally-dirty row to the server (used by the manual Save
  // button, and automatically before re-enabling auto-save). Rows save
  // sequentially, not in parallel — with auto-save off, more than one row
  // can independently hit a version conflict in the same flush, and the
  // rowConflict banner is a single slot; saving one at a time keeps which
  // conflict ends up shown deterministic instead of racing on whichever
  // PATCH happens to resolve last. A 409 on one row leaves only that row
  // dirty; every other row's save still lands. Returns whether every row
  // dirty at the time of the call actually made it to the server — a
  // conflicted/failed row stays dirty, so callers (toggleAutoSave) must
  // check this rather than assume "we awaited saveNow" means "nothing is
  // dirty anymore".
  const saveNow = useCallback((): Promise<boolean> => {
    if (saveNowStateRef.current) {
      saveNowStateRef.current.rerunRequested = true;
      return saveNowStateRef.current.promise;
    }
    if (!currentSheet) return Promise.resolve(true);
    const sheetId = currentSheet.id;

    const state: { promise: Promise<boolean>; rerunRequested: boolean } = { promise: null as any, rerunRequested: false };
    saveNowStateRef.current = state;

    async function runOnePass(): Promise<boolean> {
      const idsToSave = Array.from(dirtyRowIdsRef.current);
      if (idsToSave.length === 0) return true;
      setManualSaveStatus("saving");
      setManualSaveError(null);
      let allSucceeded = true;
      for (const rowId of idsToSave) {
        const pending = dirtyEditsRef.current.get(rowId);
        if (!pending) {
          clearRowDirty(rowId); // no pending edit recorded — nothing to save
          continue;
        }
        const { data: sentData, version: sentVersion } = pending;
        try {
          const updated = await api.updateRow(sheetId, rowId, sentData, sentVersion);
          const stillPending = dirtyEditsRef.current.get(rowId);
          const editedAgainMidFlight = !!stillPending && stillPending.data !== sentData;
          // If a newer local edit landed while this PATCH was in flight,
          // the visible row must keep showing that newer data — not the
          // (now stale) data we just sent — while still adopting `updated`'s
          // other fields (position/height/version) from the response.
          setRows((prev) => prev.map((r) => (
            r.id === rowId
              ? (editedAgainMidFlight ? { ...updated, data: stillPending!.data } : updated)
              : r
          )));
          if (editedAgainMidFlight) {
            // Keep the newer local edit queued, but adopt the fresh server
            // version so the next save's optimistic-concurrency check
            // doesn't spuriously 409 against its own prior save.
            dirtyEditsRef.current.set(rowId, { data: stillPending!.data, version: updated.version });
            allSucceeded = false;
          } else if (stillPending) {
            clearRowDirty(rowId);
          }
          // else: resolved by something else while this PATCH was in flight
          // (e.g. deleted, or its conflict already accepted) — nothing
          // further to do.
        } catch (err: any) {
          allSucceeded = false;
          if (err instanceof RowConflictError) {
            setRowConflict(err.currentRow);
          } else {
            console.error(`Failed to save sheet row ${rowId}`, err);
            // Surfaced by ManualSaveControl — without this, a non-conflict
            // failure (e.g. the sheet went read-only mid-session) left the
            // row stuck dirty with only a bare "Save failed" and no reason,
            // console-only, indistinguishable from a transient network blip.
            setManualSaveError((prev) => prev ?? (err?.response?.data?.detail || err?.message || "Failed to save changes"));
          }
        }
      }
      setManualSaveStatus(allSucceeded ? "saved" : "error");
      return allSucceeded;
    }

    async function loop(): Promise<boolean> {
      let succeeded = true;
      do {
        // Snapshot before the pass so we can tell "a row went dirty while
        // this pass was running" (must rerun — its edit was never
        // attempted) apart from "a row we just attempted is still dirty
        // because it failed/conflicted" (must NOT rerun immediately — that
        // would just hammer the same conflict in a tight loop). A plain
        // saveNow() call from elsewhere while a pass is running already
        // sets rerunRequested directly; this covers the case where a row
        // is edited (markRowDirty) without saveNow() itself being called.
        const idsBeforePass = new Set(dirtyRowIdsRef.current);
        state.rerunRequested = false;
        succeeded = await runOnePass();
        if (!state.rerunRequested) {
          const newDirtyAppeared = Array.from(dirtyRowIdsRef.current).some((id) => !idsBeforePass.has(id));
          if (newDirtyAppeared) state.rerunRequested = true;
        }
      } while (state.rerunRequested);
      return succeeded;
    }

    state.promise = loop().finally(() => { saveNowStateRef.current = null; });
    return state.promise;
  }, [currentSheet, clearRowDirty]);

  const toggleAutoSave = useCallback(async (enabled: boolean) => {
    if (!currentSheet) return;
    const sheetId = currentSheet.id;
    // Read the ref, not the dirtyRowIds state closure — a cell's blur-commit
    // (which marks it dirty) and this toggle's click can land in the same
    // gesture/render pass, and the ref is guaranteed synchronously current
    // while the state closure captured here might predate that commit.
    if (enabled && dirtyRowIdsRef.current.size > 0) {
      const flushed = await saveNow();
      // Don't turn auto-save on over an unresolved conflict/error — that
      // would unmount the only UI (ManualSaveControl) that lets the user
      // retry saving the still-dirty row, silently losing it on next load.
      // Throw (rather than silently returning) so AutoSaveToggle's catch
      // actually fires and tells the user why the toggle didn't take.
      if (!flushed) throw new Error("Could not save all pending changes — auto-save was not enabled.");
    }
    const updated = await api.updateAutoSave(sheetId, enabled);
    // The user may have navigated to a different sheet while this PATCH was
    // in flight — currentSheetIdRef always reflects the most recently
    // loaded sheet, so this discards a stale response instead of merging
    // sheet A's data onto whatever sheet is now actually loaded.
    if (currentSheetIdRef.current !== sheetId) return;
    // Merge only this field, not the whole response — toggleReadonly can be
    // clicked in the same gesture and its PATCH may resolve after this one;
    // spreading the full snapshot would silently revert whichever field
    // that other in-flight toggle had already applied.
    setCurrentSheet((prev) => (prev ? { ...prev, auto_save_enabled: updated.auto_save_enabled } : updated));
  }, [currentSheet, saveNow]);

  // Admin-only sheet lock — flushes any local-only dirty edits first (same
  // reasoning as toggleAutoSave above: once is_readonly flips true, canEdit
  // goes false for everyone including admin, so a still-dirty row could
  // never be saved again through the UI).
  const toggleReadonly = useCallback(async (enabled: boolean) => {
    if (!currentSheet) return;
    const sheetId = currentSheet.id;
    if (enabled && dirtyRowIdsRef.current.size > 0) {
      const flushed = await saveNow();
      if (!flushed) throw new Error("Could not save all pending changes — read-only mode was not enabled.");
    }
    const updated = await api.updateReadonly(sheetId, enabled);
    if (currentSheetIdRef.current !== sheetId) return;
    // Merge only this field — see toggleAutoSave's identical comment above.
    setCurrentSheet((prev) => (prev ? { ...prev, is_readonly: updated.is_readonly } : updated));
  }, [currentSheet, saveNow]);

  // Safety net for the one real data-loss risk auto-save-off introduces:
  // closing/refreshing the tab with local-only edits still unsaved.
  // KNOWN GAP: this plus SheetDetailPage's back-button confirm are the only
  // guards — in-app navigation via the sidebar or any other <Link>/navigate()
  // call, and real browser back/forward, all bypass both silently. Full
  // coverage needs react-router's useBlocker, which requires migrating the
  // app off plain <BrowserRouter> onto a data router (App.tsx) — out of
  // scope here; accepted for now alongside the single-editor tradeoff above.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const removeRow = useCallback(async (rowId: number) => {
    if (!currentSheet) return;
    await trackSave(() => api.deleteRow(currentSheet.id, rowId));
    // A deleted row can never be saved — drop any pending local edit for it
    // rather than leaving hasUnsavedChanges stuck true for a row that's gone.
    clearRowDirty(rowId);
    await loadRowsPage(currentSheet.id, rowsPage);
  }, [currentSheet, loadRowsPage, rowsPage, trackSave, clearRowDirty]);

  // Undo for a deleted row — reactivates the exact same soft-deleted row
  // (rows have no natural unique key to re-create-in-place the way a
  // column's key does), preserving its original data/position/id.
  const restoreRow = useCallback(async (rowId: number) => {
    if (!currentSheet) return;
    await trackSave(() => api.restoreRow(currentSheet.id, rowId));
    await loadRowsPage(currentSheet.id, rowsPage);
  }, [currentSheet, loadRowsPage, rowsPage, trackSave]);

  // Drag-and-drop row reordering — scoped to whichever rows the caller
  // passes (typically just the current page), in their new order.
  const reorderRows = useCallback(async (rowIds: number[]) => {
    if (!currentSheet) return;
    await trackSave(() => api.reorderRows(currentSheet.id, rowIds));
    await loadRowsPage(currentSheet.id, rowsPage);
  }, [currentSheet, loadRowsPage, rowsPage, trackSave]);

  // Called once when a row-height drag ends (the drag itself is
  // live-previewed via local component state, not this call) — patches the
  // row in place rather than reloading the page, so a resize doesn't reset
  // scroll position or fight any other in-flight edit.
  const editRowHeight = useCallback(async (rowId: number, height: number | null) => {
    if (!currentSheet) return;
    const updated = await trackSave(() => api.updateRowHeight(currentSheet.id, rowId, height));
    // If this row also has an unsaved local edit, withDirtyOverlay keeps
    // its data/version untouched — see its own comment for why.
    setRows((prev) => prev.map((r) => (r.id === rowId ? withDirtyOverlay(updated) : r)));
  }, [currentSheet, trackSave]);

  const importCsvPreview = useCallback(async (file: File) => {
    if (!currentSheet) throw new Error("No sheet loaded");
    return api.importCsv(currentSheet.id, file, true);
  }, [currentSheet]);

  const importCsvCommit = useCallback(async (file: File) => {
    if (!currentSheet) throw new Error("No sheet loaded");
    const result = await api.importCsv(currentSheet.id, file, false);
    // A commit may have created new columns for previously-unmatched
    // headers, so the column list needs refreshing too, not just rows.
    await Promise.all([reloadColumns(), loadRowsPage(currentSheet.id, 1)]);
    setRowsPage(1);
    return result;
  }, [currentSheet, loadRowsPage, reloadColumns]);

  const triggerExportXlsx = useCallback(async () => {
    if (!currentSheet) return;
    const blob = await api.exportXlsx(currentSheet.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentSheet.name}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [currentSheet]);

  const loadShares = useCallback(async () => {
    if (!currentSheet) return;
    setShares(await api.listShares(currentSheet.id));
  }, [currentSheet]);

  const loadAssignableEmployees = useCallback(async () => {
    setAssignableEmployees(await api.listAssignableEmployees());
  }, []);

  const loadCheckpoints = useCallback(async () => {
    if (!currentSheet) return;
    setCheckpoints(await api.listCheckpoints(currentSheet.id));
  }, [currentSheet]);

  // Not wrapped in trackSave — that drives the header's grid-only "Saving…/
  // Last saved at" indicator (cell edits, row/column add/remove, paste);
  // saving a checkpoint isn't a grid mutation and firing it here would
  // misleadingly flip that indicator for an unrelated action.
  const saveCheckpoint = useCallback(async (label: string | null) => {
    if (!currentSheet) return;
    await api.createCheckpoint(currentSheet.id, label);
    await loadCheckpoints();
  }, [currentSheet, loadCheckpoints]);

  const removeCheckpoint = useCallback(async (checkpointId: number) => {
    if (!currentSheet) return;
    await api.deleteCheckpoint(currentSheet.id, checkpointId);
    await loadCheckpoints();
  }, [currentSheet, loadCheckpoints]);

  const purgeCheckpoints = useCallback(async (olderThanDays: number): Promise<number> => {
    if (!currentSheet) return 0;
    const { deleted } = await api.purgeCheckpointsOlderThan(currentSheet.id, olderThanDays);
    await loadCheckpoints();
    return deleted;
  }, [currentSheet, loadCheckpoints]);

  // Restoring rewrites the sheet's whole columns + rows set server-side, so
  // the detail view is reloaded from scratch afterward (also resets to page
  // 1, since the restored row count may no longer match where we were).
  // Also not wrapped in trackSave (see saveCheckpoint above). The refresh
  // runs in `finally` — even if the restore call itself fails, the grid
  // must still reload rather than keep showing stale pre-restore data,
  // since the backend may have already committed a partial/actual change.
  const restoreCheckpoint = useCallback(async (checkpointId: number) => {
    if (!currentSheet) return;
    let restoreError: unknown;
    try {
      await api.restoreCheckpoint(currentSheet.id, checkpointId);
    } catch (err) {
      restoreError = err;
    }
    // The refresh's own errors are swallowed here rather than thrown —
    // otherwise a flaky reload immediately after a genuine restore failure
    // would silently replace that original error (plain try/finally
    // semantics: whichever throws last wins), leaving the caller unable to
    // tell the user what actually went wrong.
    try {
      await Promise.all([loadSheetDetail(currentSheet.id), loadCheckpoints()]);
    } catch (refreshErr) {
      console.error("Failed to refresh sheet after a checkpoint restore attempt", refreshErr);
    }
    if (restoreError) throw restoreError;
  }, [currentSheet, loadSheetDetail, loadCheckpoints]);

  // Fires every share request in parallel and reloads the share list
  // exactly once afterward, instead of once per employee.
  const addShares = useCallback(async (employeeIds: number[], permission: SharePermission) => {
    if (!currentSheet || employeeIds.length === 0) return;
    await Promise.all(employeeIds.map((id) => api.shareSheet(currentSheet.id, id, permission)));
    await loadShares();
  }, [currentSheet, loadShares]);

  const editSharePermission = useCallback(async (shareId: number, permission: SharePermission) => {
    if (!currentSheet) return;
    await api.updateSharePermission(currentSheet.id, shareId, permission);
    await loadShares();
  }, [currentSheet, loadShares]);

  const removeShare = useCallback(async (shareId: number) => {
    if (!currentSheet) return;
    await api.unshareSheet(currentSheet.id, shareId);
    await loadShares();
  }, [currentSheet, loadShares]);

  const value = useMemo<SheetsContextValue>(() => ({
    isAdmin, canEdit, canManageColumns,
    sheets, totalCount, totalPages, page, loading, error, search, categoryFilter, tagFilter,
    setPage, setSearch, setCategoryFilter, setTagFilter, refresh,
    categories, refreshCategories, createCategory, renameCategory, removeCategory,
    checkSimilarSheets, checkSimilarCategories,
    createSheet, deleteSheet,
    currentSheet, columns, shares, assignableEmployees, rows, rowsPage, rowsTotalPages,
    rowConflict, clearRowConflict, acceptRowConflict, detailLoading, detailError, saveStatus, saveError, lastSavedAt,
    hasUnsavedChanges, manualSaveStatus, manualSaveError, saveNow, toggleAutoSave, toggleReadonly,
    loadSheetDetail, updateSheetMeta, setRowsPage,
    addColumn, editColumn, removeColumn, reorderColumns,
    addRow, pasteNewRows, editRow, removeRow, restoreRow, reorderRows, editRowHeight,
    importCsvPreview, importCsvCommit, triggerExportXlsx,
    loadShares, addShares, editSharePermission, removeShare, loadAssignableEmployees,
    checkpoints, loadCheckpoints, saveCheckpoint, removeCheckpoint, purgeCheckpoints, restoreCheckpoint,
  }), [
    isAdmin, canEdit, canManageColumns,
    sheets, totalCount, totalPages, page, loading, error, search, categoryFilter, tagFilter,
    setSearch, setCategoryFilter, setTagFilter, refresh,
    categories, refreshCategories, createCategory, renameCategory, removeCategory,
    checkSimilarSheets, checkSimilarCategories,
    createSheet, deleteSheet,
    currentSheet, columns, shares, assignableEmployees, rows, rowsPage, rowsTotalPages,
    rowConflict, clearRowConflict, acceptRowConflict, detailLoading, detailError, saveStatus, saveError, lastSavedAt,
    hasUnsavedChanges, manualSaveStatus, manualSaveError, saveNow, toggleAutoSave, toggleReadonly,
    loadSheetDetail, updateSheetMeta,
    addColumn, editColumn, removeColumn, reorderColumns,
    addRow, pasteNewRows, editRow, removeRow, restoreRow, reorderRows, editRowHeight,
    importCsvPreview, importCsvCommit, triggerExportXlsx,
    loadShares, addShares, editSharePermission, removeShare, loadAssignableEmployees,
    checkpoints, loadCheckpoints, saveCheckpoint, removeCheckpoint, purgeCheckpoints, restoreCheckpoint,
  ]);

  return <SheetsContext.Provider value={value}>{children}</SheetsContext.Provider>;
}

export function useSheets(): SheetsContextValue {
  const ctx = useContext(SheetsContext);
  if (!ctx) throw new Error("useSheets must be used within SheetsProvider");
  return ctx;
}
